import type { BoardState } from './board';
import type { Cell, Hazard, HazardType, PlayerState } from './types';
import type { EventLog } from './eventLog';
import type { Rng } from './rng';
import { SCORING, LIVES_START } from './config';
import { DIRS, neighborId, manhattan, bfsNextDir } from './grid';

export const HAZARD_STEP_MS: Record<HazardType, number> = {
  crawler: 520,
  hunter: 380,
  liar: 900,
  scrambler: 1100,
};

/** How long a Liar's misleading relabel persists before it reverts. */
const LIAR_RELABEL_MS = 3000;
// 2026-07-03 owner-feedback soften ("too hard/impossible"): +50% post-hit immunity (800→1200) so a
// respawn can never chain into an instant second death. The anti-camp pause is bumped in lockstep so
// it stays strictly greater than the i-frame window (no hazard re-hits before control returns).
const RESPAWN_IFRAME_MS = 1200;
const RESPAWN_HAZARD_PAUSE_MS = 1600;
const RESPAWN_SAFE_RADIUS = 3;

/** Crawler-style roam: mostly toward the target, sometimes random. Deterministic via rng. */
function crawlerNext(state: BoardState, pos: number, target: number, rng: Rng): number | null {
  const moves = DIRS.map((d) => neighborId(pos, d, state.rows, state.cols)).filter(
    (id): id is number => id !== null && hazardPassable(state, id),
  );
  if (moves.length === 0) return null;
  if (rng.chance(0.7)) {
    moves.sort((a, b) => manhattan(a, target, state.cols) - manhattan(b, target, state.cols));
    return moves[0] as number;
  }
  return rng.pick(moves);
}

/** Swap the CONTENT (identity) of two cells in place — positions/eaten/armed state stay put. */
function swapContent(a: Cell, b: Cell): void {
  const keys: (keyof Cell)[] = ['kind', 'text', 'displayText', 'difficulty', 'explanation', 'lying', 'lyingUntilMs'];
  for (const k of keys) {
    const t = a[k];
    (a[k] as unknown) = b[k];
    (b[k] as unknown) = t;
  }
}

/** Hazards respect walls but ignore armed lockouts (they are the threat, not a player). */
function hazardPassable(state: BoardState, id: number): boolean {
  return state.cells[id].kind !== 'wall';
}

function nearestPlayerPos(hazard: Hazard, players: PlayerState[], cols: number): number | null {
  let best: number | null = null;
  let bestD = Infinity;
  for (const p of players) {
    if (!p.alive) continue;
    const d = manhattan(hazard.pos, p.pos, cols);
    if (d < bestD) {
      bestD = d;
      best = p.pos;
    }
  }
  return best;
}

export function spawnHazard(
  state: BoardState,
  type: HazardType,
  id: number,
  players: PlayerState[],
  nowMs: number,
  rng: Rng,
  log: EventLog,
): Hazard {
  const total = state.rows * state.cols;
  const playerCells = new Set(players.map((p) => p.pos));
  const candidates: number[] = [];
  for (let i = 0; i < total; i++) {
    if (!hazardPassable(state, i) || playerCells.has(i)) continue;
    const farEnough = players.every((p) => manhattan(i, p.pos, state.cols) >= 3);
    if (farEnough) candidates.push(i);
  }
  const pool = candidates.length > 0 ? candidates : Array.from({ length: total }, (_, i) => i).filter((i) => hazardPassable(state, i) && !playerCells.has(i));
  const pos = pool.length > 0 ? rng.pick(pool) : 0;
  const h: Hazard = { id, type, pos, nextStepMs: nowMs + HAZARD_STEP_MS[type], pausedUntilMs: 0, stepCount: 0 };
  log.push('hazardSpawn', nowMs, { hazard: id, cell: pos });
  return h;
}

/** Advance one hazard if its cadence is due. crawler · hunter · liar · scrambler. */
export function stepHazard(
  state: BoardState,
  h: Hazard,
  players: PlayerState[],
  nowMs: number,
  rng: Rng,
  log: EventLog,
): void {
  if (nowMs < h.pausedUntilMs || nowMs < h.nextStepMs) return;
  h.nextStepMs = nowMs + HAZARD_STEP_MS[h.type];

  const target = nearestPlayerPos(h, players, state.cols);
  if (target === null) return;

  // Movement.
  if (h.type === 'hunter') {
    const dir = bfsNextDir(h.pos, target, state.rows, state.cols, (id) => hazardPassable(state, id));
    const next = dir ? neighborId(h.pos, dir, state.rows, state.cols) : null;
    h.stepCount += 1;
    if (h.stepCount % 3 === 0) h.pausedUntilMs = nowMs + 1000; // beatable: pauses every 3 cells
    if (next !== null) {
      h.pos = next;
      log.push('hazardStep', nowMs, { hazard: h.id, cell: next });
    }
    return;
  }

  // crawler / liar / scrambler all roam crawler-style; liar + scrambler also tamper with cells.
  const next = crawlerNext(state, h.pos, target, rng);
  if (next !== null) {
    h.pos = next;
    log.push('hazardStep', nowMs, { hazard: h.id, cell: next });
  }
  h.stepCount += 1;

  if (h.type === 'liar') {
    // LIE on every other step, at most one lie active at a time. Swap displayText between a
    // non-eaten correct and a non-eaten decoy so BOTH read wrong; the true `kind` never changes,
    // so eating still scores by truth. The `lying` flag is the renderer's shimmer tell.
    if (h.stepCount % 2 === 0 && !state.cells.some((c) => c.lying)) {
      const correct = state.cells.filter((c) => c.kind === 'correct' && !c.eaten && !c.armed);
      const decoy = state.cells.filter((c) => c.kind === 'decoy' && !c.eaten && !c.armed);
      if (correct.length > 0 && decoy.length > 0) {
        const a = rng.pick(correct);
        const b = rng.pick(decoy);
        const tmp = a.displayText;
        a.displayText = b.displayText;
        b.displayText = tmp;
        a.lying = true;
        a.lyingUntilMs = nowMs + LIAR_RELABEL_MS;
        b.lying = true;
        b.lyingUntilMs = nowMs + LIAR_RELABEL_MS;
        log.push('hazardStep', nowMs, { hazard: h.id, cell: a.id, value: b.id });
      }
    }
  } else if (h.type === 'scrambler') {
    // SWAP the content of two non-eaten, non-armed cells (answers change position). Deterministic.
    if (h.stepCount % 2 === 0) {
      const pool = state.cells.filter((c) => c.kind !== 'wall' && !c.eaten && !c.armed);
      if (pool.length >= 2) {
        const a = rng.pick(pool);
        let b = rng.pick(pool);
        let guard = 0;
        while (b.id === a.id && guard++ < 8) b = rng.pick(pool);
        if (b.id !== a.id) {
          swapContent(a, b);
          log.push('hazardStep', nowMs, { hazard: h.id, cell: a.id, value: b.id });
        }
      }
    }
  }
}

/** A correct eat emits a 1-cell purge pulse that stuns adjacent hazards. */
export function purgeStun(state: BoardState, hazards: Hazard[], cellId: number, nowMs: number): void {
  for (const h of hazards) {
    if (manhattan(h.pos, cellId, state.cols) <= 1) {
      h.pausedUntilMs = Math.max(h.pausedUntilMs, nowMs + SCORING.purgeStunMs);
    }
  }
}

function relocateHazardAfterRespawn(
  state: BoardState,
  hit: Hazard,
  hazards: Hazard[],
  players: PlayerState[],
  respawnCell: number,
  nowMs: number,
  log: EventLog,
): void {
  const pauseUntil = nowMs + RESPAWN_HAZARD_PAUSE_MS;
  hit.pausedUntilMs = Math.max(hit.pausedUntilMs, pauseUntil);
  hit.nextStepMs = Math.max(hit.nextStepMs, pauseUntil);

  const blocked = new Set<number>([state.humanStart, state.aiStart]);
  for (const p of players) {
    if (p.alive) blocked.add(p.pos);
  }
  for (const h of hazards) {
    if (h.id !== hit.id) blocked.add(h.pos);
  }

  let best: number | null = null;
  let bestDistance = -1;
  const total = state.rows * state.cols;
  for (let i = 0; i < total; i += 1) {
    if (!hazardPassable(state, i) || blocked.has(i)) continue;
    const distanceFromRespawn = manhattan(i, respawnCell, state.cols);
    if (distanceFromRespawn < RESPAWN_SAFE_RADIUS) continue;
    const clearOfPlayers = players.every((p) => !p.alive || manhattan(i, p.pos, state.cols) >= 2);
    if (!clearOfPlayers) continue;
    if (distanceFromRespawn > bestDistance) {
      best = i;
      bestDistance = distanceFromRespawn;
    }
  }

  // Saturation fallback: if nothing met the safe-radius/clearance AND this hazard is squatting on the
  // respawn tile, it MUST still vacate — otherwise it re-hits the player the instant i-frames lapse.
  // Relax the radius/clearance and take the farthest passable, non-respawn, player-free cell.
  if (best === null && hit.pos === respawnCell) {
    for (let i = 0; i < total; i += 1) {
      if (!hazardPassable(state, i) || i === respawnCell) continue;
      if (players.some((p) => p.alive && p.pos === i)) continue;
      const d = manhattan(i, respawnCell, state.cols);
      if (d > bestDistance) {
        best = i;
        bestDistance = d;
      }
    }
  }

  if (best !== null && best !== hit.pos) {
    hit.pos = best;
    log.push('hazardStep', nowMs, { hazard: hit.id, cell: best });
  }
}

/** Hazard on a player's cell costs a life + i-frames + reposition to start. */
export function resolveHazardHits(
  state: BoardState,
  hazards: Hazard[],
  players: PlayerState[],
  nowMs: number,
  log: EventLog,
): void {
  // Snapshot EVERY hit before applying any consequence/relocation. The human's anti-camp pushback
  // (below) can move a hazard OFF the AI's cell, so detecting per-player inside the apply loop would let
  // the AI dodge a simultaneous hit it should have taken. Detection scans players in id order, no rng —
  // deterministic, and emits no events, so the event-log order is unchanged.
  const hits: { p: PlayerState; hit: Hazard }[] = [];
  for (const p of players) {
    if (!p.alive || nowMs < p.stunnedUntilMs) continue;
    const hit = hazards.find((h) => h.pos === p.pos);
    if (hit) hits.push({ p, hit });
  }
  // Hazards purged this pass by a CHOMP SURGE — spliced out of the live array AFTER the loop so the
  // in-loop anti-camp relocate still sees a stable set (a purged hazard leaving early can't misalign it).
  const purgedIds = new Set<number>();
  for (const { p, hit } of hits) {
    // (1) CHOMP SURGE (temp offense) — checked FIRST, so a surging player purges the hazard even while
    //     standing on safe ground. Destroy the hazard, award the flat purge bonus (integer, no combo
    //     interaction), leave the player wholly untouched (no life loss / stun / respawn).
    if (nowMs < p.surgeUntilMs) {
      p.score += SCORING.surgePurgeBonus;
      log.push('hazardPurged', nowMs, { player: p.id, hazard: hit.id, value: SCORING.surgePurgeBonus });
      purgedIds.add(hit.id);
      continue;
    }
    // (2) SAFE GROUND (protection) — standing on ANY eaten CORRECT cell (any eater; tile art doesn't
    //     distinguish the owner, so any-eater is the legible rule) makes the tile safe: the hazard
    //     passes through/over and the hit is skipped entirely (no life loss, no event).
    const standing = state.cells[p.pos];
    if (standing.eaten && standing.kind === 'correct') continue;
    // (3) Normal hit — a life + i-frames + reposition to start.
    p.lives -= 1;
    p.combo = 0;
    p.armedTraversals = 0;
    p.stunnedUntilMs = nowMs + RESPAWN_IFRAME_MS; // brief knockback / i-frames
    log.push('hazardHit', nowMs, { player: p.id, hazard: hit.id, value: p.lives });
    const respawnCell = p.id === 0 ? state.humanStart : state.aiStart;
    p.pos = respawnCell;
    if (p.lives <= 0) {
      p.alive = false;
    }
    // Anti-camp: when the HUMAN survives a hit, push back EVERY hazard from the spawn corner — not
    // just the one that hit — so the rest don't immediately re-home on the fixed respawn cell and
    // pin the player. Sequential so each hazard's blocked-set sees the already-moved hazards, and
    // each gets the 1200ms pause (> the 800ms i-frame) so none can re-hit before control returns.
    // No rng draw / no new event type — relocateHazardAfterRespawn is pure integer math, so the
    // determinism ledger is unchanged. The AI keeps the single-hazard relocate: its spawn is the
    // OPPOSITE corner, so an all-hazard push from there would dump hazards onto the human.
    if (p.id === 0 && p.lives > 0) {
      for (const h of hazards) {
        relocateHazardAfterRespawn(state, h, hazards, players, respawnCell, nowMs, log);
      }
    } else {
      relocateHazardAfterRespawn(state, hit, hazards, players, respawnCell, nowMs, log);
    }
  }
  // Remove purged hazards from the live array (reverse index — order-stable, deterministic).
  if (purgedIds.size > 0) {
    for (let i = hazards.length - 1; i >= 0; i -= 1) {
      if (purgedIds.has((hazards[i] as Hazard).id)) hazards.splice(i, 1);
    }
  }
}

export { LIVES_START };
